<template>
  <portal to="modal-destination" class="unselectable">
    <div class="relative h-screen w-screen  " v-if="isShowLinkEditorModalV3">
      <div
        class="aha-modal__backdrop"
        @click="hideModal()"
      />
      <div
        class=" link-modal"
      >
        <div class="flex justify-between align-middle mb-4 z-10">
          <div class="font-bold text-base">
            Insert/ Edit Link
          </div>
          <aha-icon
            name="close"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            @click="hideModal"
            class="cursor-pointer text-base-80"
          />
        </div>
        <div class="space-y-3">
          <div class="space-y-1">
            <div>URL</div>
            <aha-input
              autofocus
              v-model="link"
              placeholder="Link input"
              @keyup.enter="saveLink"
            />
            <div class="mt-1 text-sm text-red-100" v-if="!isValidLink">
              Please enter a valid link
            </div>
          </div>
          <div class="space-y-1">
            <div>Text to display</div>
            <aha-input
              v-model="textDisplay"
              placeholder="Text to display"
              @keyup.enter="saveLink"
            />
          </div>
          <div class="space-y-1">
            <div>Open link in...</div>
            <aha-select v-model="target">
              <aha-select-option
                value="blank"
              >
                {{ $i18nPresenter.t('New window') }}
              </aha-select-option>
              <aha-select-option
                value="_self"
              >
                {{ $i18nPresenter.t('Current window') }}
              </aha-select-option>
            </aha-select>
          </div>

          <div class="flex justify-end align-middle toolbox-action ">
            <div class="flex space-x-3">
              <aha-antd-button @click="hideModal" variant="text">
                Cancel
              </aha-antd-button>
              <aha-antd-button @click="saveLink" :disabled="!isValidLink">
                Save
              </aha-antd-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </portal>
</template>
<script>
import { mapState } from 'vuex'
import configs from '@/config'
import { restoreOriginalLinks, deleteFakeDom } from '@/utils/dom.utils'

export default {
  data() {
    return {
      link: '',
      target: 'blank',
      textDisplay: '',
      isUsingV3: false,
      isSaving: false,
    }
  },
  computed: {
    ...mapState({
      isShowLinkEditorModalV3: (state) => state.isShowLinkEditorModalV3,
    }),
    isValidLink() {
      if (this.link.length === 0) {
        return true
      }
      // http text have 1 one character
      if ((this.link.match(/http/g) || []).length > 1) {
        return false
      }
      return this.link.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g)
    },
  },
  watch: {
    isShowLinkEditorModalV3() {
      if (this.isShowLinkEditorModalV3) {
        // Check if the fake selection has V3 data attributes
        const fakeSelection = this.domSelection()
        this.isUsingV3 = fakeSelection && fakeSelection.hasAttribute('data-original-text')

        if (this.isUsingV3) {
          this.getLinkInFakeDomV3()
        } else {
          this.getLinkInFakeDom()
        }
      }
    },
  },
  mounted() {
    document.addEventListener('keyup', (e) => {
      if (configs.EXIT_KEYS.includes(e.code)) {
        this.hideModal()
      }
    })
  },
  destroyed() {
    document.removeEventListener('keyup', (e) => {
      if (configs.EXIT_KEYS.includes(e.code)) {
        this.hideModal()
      }
    })
  },
  methods: {
    domSelection() {
      return document.querySelector('.fake-selection')
    },
    removeLink() {
      this.domSelection().closest('a').outerHTML = this.domSelection().textContent
      this.hideModal()
    },
    getLinkInFakeDom() {
      if (this.domSelection()) {
        const hrefDom = this.domSelection().closest('a')
        this.textDisplay = this.domSelection().textContent
        if (hrefDom) {
          this.link = hrefDom.getAttribute('href')
          this.target = hrefDom.getAttribute('target')
        }
      }
    },

    getLinkInFakeDomV3() {
      const fakeSelection = this.domSelection()
      if (fakeSelection) {
        // Check if we have data attributes from the improved selection
        const originalHref = fakeSelection.getAttribute('data-original-href')
        const originalTarget = fakeSelection.getAttribute('data-original-target')
        const originalText = fakeSelection.getAttribute('data-original-text')

        if (originalHref !== null) {
          // We're editing an existing link
          this.link = originalHref
          this.target = originalTarget || 'blank'
          this.textDisplay = originalText || fakeSelection.textContent
        } else {
          // New link creation
          this.textDisplay = fakeSelection.textContent || ''
          // If the text is just a placeholder (non-breaking space), clear it
          if (this.textDisplay === '\u00A0') { // &nbsp; character
            this.textDisplay = ''
          }
          this.link = ''
          this.target = 'blank'
        }
      }
    },

    hideModal() {
      this.$store.state.isShowLinkEditorModalV3 = false

      // Only restore original links if we're canceling (not saving)
      if (!this.isSaving && this.link !== this.textDisplay) {
        restoreOriginalLinks()
      } else {
        deleteFakeDom()
      }

      this.resetState()
    },
    resetState() {
      this.link = ''
      this.target = 'blank'
      this.textDisplay = ''
      this.isUsingV3 = false
      this.isSaving = false
    },
    formatHttp(text) {
      // if text is not a link, return https
      if (!text.includes('http')) {
        return `https://${text}`
      }
      return text
    },
    saveLink() {
      if (!this.isValidLink) {
        return
      }

      this.isSaving = true // Mark that we're saving, not canceling

      const selectionDoms = document.querySelectorAll('.fake-selection')

      Array.from(selectionDoms).map((selectionDom) => {
        if (this.link.length === 0) {
          // No link URL provided - just replace with plain text
          if (selectionDom.closest('a')) {
            // Remove existing link, keep only text
            selectionDom.closest('a').outerHTML = this.textDisplay
          } else {
            // Just replace fake selection with text
            selectionDom.outerHTML = this.textDisplay
          }
        } else {
          // Link URL provided - create or update the link
          const a = document.createElement('a')
          a.innerHTML = this.textDisplay || ''
          a.type = 'link'
          a.href = this.formatHttp(this.link)
          a.classList.add('text-hyper-link')
          a.target = this.target || 'blank'

          // Replace the fake selection with the actual link
          selectionDom.outerHTML = a.outerHTML
        }
        return null
      })

      this.hideModal()
    },

    sanitize(url, protocols) {
      const anchor = document.createElement('a')
      anchor.href = url
      const protocol = anchor.href.slice(0, anchor.href.indexOf(':'))
      return protocols.indexOf(protocol) > -1
    },
  },

}
</script>
<style lang="scss" scoped>
.unselectable {
  -khtml-user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
.link-modal{
  @apply   rounded-md  w-1/4 z-10 flex flex-col absolute top-1/2 left-1/2 bg-base-0 shadow-2xl border border-base-30 p-4;
  transform: translate(-50%, -50%);
}
.aha-modal__backdrop{
  @apply absolute inset-0;
}
</style>
